import { useCallback, useRef } from 'react';
import { updateFactureExterne, updateFactureInterne } from '../../services/airtable';
import { VERCEL_FUNCTIONS } from '../../config/vercel';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

const QONTO_STEP_IDS = ['qontoEmit', 'qontoWait', 'qontoDropbox', 'qontoDrive', 'qontoEmail'];
// Ces trois étapes tournent en parallèle (aucune dépendance entre elles) : pour la reprise
// après erreur, elles sont traitées comme un seul palier — si l'une d'elles échoue, "Réessayer"
// relance les trois ensemble plutôt que de tenter un suivi indépendant par étape.
const PARALLEL_QONTO_STEPS = ['qontoDropbox', 'qontoDrive', 'qontoEmail'];

// Factures internes (GIE -> Consultants) : mêmes principes que pour les factures externes
// ci-dessus, mais pas d'étape "db" séparée (le chargement est fait côté serveur dans l'étape
// "emit") ni d'étape email (pas d'envoi au consultant pour l'instant).
const QONTO_INTERNE_STEP_IDS = ['qontoEmit', 'qontoWait', 'qontoDropbox', 'qontoDrive'];
const PARALLEL_QONTO_INTERNE_STEPS = ['qontoDropbox', 'qontoDrive'];

/**
 * useFacturationHandlers - Handlers pour les actions de facturation
 *
 * Extrait de FacturationTable.js pour réduire sa taille.
 * Gère toutes les interactions utilisateur (tri, envoi email, paiement, etc.)
 *
 * @param {Object} params
 * @param {Object} params.state - État du composant
 * @param {Function} params.dispatch - Dispatch du reducer
 * @param {Object} params.ACTIONS - Actions du reducer
 * @param {Function} params.loadData - Fonction de rechargement des données
 * @param {Function} params.showToast - Fonction d'affichage des toasts
 * @param {Array} params.prestataires - Liste des prestataires
 * @param {Object} params.consultantsMap - Map des consultants
 * @param {Function} params.setProgressModal - Setter pour la modale de progression
 * @param {Function} params.setFacturesExternes - Setter pour les factures externes
 * @param {Function} params.setFacturesInternes - Setter pour les factures internes
 * @param {Function} params.setError - Setter pour les erreurs
 */
export function useFacturationHandlers({
  state,
  dispatch,
  ACTIONS,
  loadData,
  showToast,
  prestataires,
  consultantsMap,
  setProgressModal,
  setFacturesExternes,
  setFacturesInternes,
  setError
}) {
  const {
    activeTab,
    sortColumn,
    sortDirection,
    dateEmissionFacture,
    dateEmissionPrestataire,
    emailFacture,
    selectedPaymentFacture,
    editingFactureInterne,
    selectedDownloadFactureInterne,
    pdfUrl
  } = state;

  // === HELPERS ===

  const updateStep = useCallback((stepId, status, subLabel = null) => {
    setProgressModal(prev => ({
      ...prev,
      steps: prev.steps.map(s => s.id === stepId ? { ...s, status, ...(subLabel !== null && { subLabel }) } : s)
    }));
  }, [setProgressModal]);

  // Mémorise où reprendre en cas d'échec d'une étape Qonto (garde-fou pour le bouton
  // "Réessayer" de la modale : ne relance pas tout le processus, juste ce qui a échoué).
  const qontoRetryStateRef = useRef(null);

  const disableRemainingQontoSteps = useCallback((fromIndex, status) => {
    for (let i = fromIndex; i < QONTO_STEP_IDS.length; i++) updateStep(QONTO_STEP_IDS[i], status);
  }, [updateStep]);

  /**
   * Exécute (ou reprend) la séquence d'émission Qonto : chargement des données -> émission
   * -> attente PDF -> upload Dropbox -> upload Drive. Chaque étape passée avec succès lors
   * d'un appel précédent est réutilisée via `resume` plutôt que rejouée.
   *
   * @param {string} numeroFacture
   * @param {string} dateEmission
   * @param {Object} [resume] - { qontoInvoiceId, attachmentId, resumeFrom } pour reprendre
   *   après un échec, au lieu de repartir de "Chargement des données".
   */
  const runQontoFlow = useCallback(async (numeroFacture, dateEmission, resume = null) => {
    const callEinvoicingStep = async (step, extra = {}) => fetchWithAuth(VERCEL_FUNCTIONS.E_INVOICING, {
      method: 'POST',
      body: JSON.stringify({ step, numeroFacture, dateEmission, ...extra })
    });

    let qontoInvoiceId = resume?.qontoInvoiceId || null;
    let attachmentId = resume?.attachmentId || null;
    const resumeFrom = resume?.resumeFrom || 'db';
    const resumeOrder = ['db', 'qontoEmit', 'qontoWait', 'parallel'];
    const normalize = (step) => (PARALLEL_QONTO_STEPS.includes(step) ? 'parallel' : step);
    const shouldRun = (step) => resumeOrder.indexOf(normalize(step)) >= resumeOrder.indexOf(normalize(resumeFrom));

    try {
      // Étape "db"
      if (shouldRun('db')) {
        updateStep('db', 'in_progress');
        const loadResponse = await fetchWithAuth('/.netlify/functions/getFactureExterneForEmission', {
          method: 'POST',
          body: JSON.stringify({ numeroFacture })
        });
        const loadResult = await loadResponse.json();

        if (!loadResponse.ok) {
          console.warn('[e-invoicing] Chargement des données échoué:', loadResult.error);
          updateStep('db', 'error');
          disableRemainingQontoSteps(0, 'error');
          qontoRetryStateRef.current = { numeroFacture, dateEmission, resumeFrom: 'db' };
          return;
        }
        updateStep('db', 'completed');
      } else {
        updateStep('db', 'completed');
      }

      // Étape "qontoEmit"
      if (shouldRun('qontoEmit')) {
        updateStep('qontoEmit', 'in_progress');
        const emitResponse = await callEinvoicingStep('emit');
        const emitData = await emitResponse.json();

        if (!emitResponse.ok) {
          if (emitResponse.status === 400 && /désactivée/.test(emitData.error || '')) {
            disableRemainingQontoSteps(0, 'disabled');
            qontoRetryStateRef.current = null;
            return;
          }
          updateStep('qontoEmit', 'error');
          disableRemainingQontoSteps(1, 'error');
          console.warn('[e-invoicing] Erreur emit:', emitData.error);
          qontoRetryStateRef.current = { numeroFacture, dateEmission, resumeFrom: 'qontoEmit' };
          return;
        }
        updateStep('qontoEmit', 'completed');
        qontoInvoiceId = emitData.qontoInvoiceId;
        attachmentId = emitData.attachmentId || attachmentId;
      } else {
        updateStep('qontoEmit', 'completed');
      }

      // Étape "qontoWait"
      if (shouldRun('qontoWait')) {
        updateStep('qontoWait', 'in_progress');
        const waitResponse = await callEinvoicingStep('wait-pdf', { qontoInvoiceId });
        const waitData = await waitResponse.json();

        if (!waitResponse.ok) {
          updateStep('qontoWait', 'error');
          disableRemainingQontoSteps(2, 'error');
          console.warn('[e-invoicing] Erreur wait-pdf:', waitData.error);
          qontoRetryStateRef.current = { numeroFacture, dateEmission, qontoInvoiceId, resumeFrom: 'qontoWait' };
          return;
        }
        updateStep('qontoWait', 'completed');
        attachmentId = waitData.attachmentId || attachmentId;
      } else {
        updateStep('qontoWait', 'completed');
      }

      // Étapes "qontoDropbox", "qontoDrive" et "qontoEmail" : indépendantes les unes des
      // autres, lancées en parallèle (pas besoin d'attendre l'une pour démarrer l'autre).
      let failedAt = null;

      const runDropbox = async () => {
        if (!shouldRun('qontoDropbox')) { updateStep('qontoDropbox', 'completed'); return; }
        updateStep('qontoDropbox', 'in_progress');
        try {
          const dropboxResponse = await callEinvoicingStep('upload-dropbox', { qontoInvoiceId, attachmentId });
          const dropboxData = await dropboxResponse.json();

          if (!dropboxResponse.ok) {
            updateStep('qontoDropbox', 'error');
            failedAt = failedAt || 'qontoDropbox';
            console.warn('[e-invoicing] Erreur upload-dropbox:', dropboxData.error);
          } else {
            updateStep('qontoDropbox', dropboxData.configured ? 'completed' : 'disabled');
          }
        } catch (err) {
          updateStep('qontoDropbox', 'error');
          failedAt = failedAt || 'qontoDropbox';
          console.error('[e-invoicing] Erreur réseau upload-dropbox:', err);
        }
      };

      const runDrive = async () => {
        if (!shouldRun('qontoDrive')) { updateStep('qontoDrive', 'completed'); return; }
        updateStep('qontoDrive', 'in_progress');
        try {
          const driveResponse = await callEinvoicingStep('upload-drive', { qontoInvoiceId, attachmentId });
          const driveData = await driveResponse.json();

          if (!driveResponse.ok) {
            updateStep('qontoDrive', 'error');
            failedAt = failedAt || 'qontoDrive';
            console.warn('[e-invoicing] Erreur upload-drive:', driveData.error);
          } else {
            updateStep('qontoDrive', 'completed');
            console.log('[e-invoicing] PDF Qonto déposé sur Drive:', driveData.driveFileId);
          }
        } catch (err) {
          updateStep('qontoDrive', 'error');
          failedAt = failedAt || 'qontoDrive';
          console.error('[e-invoicing] Erreur réseau upload-drive:', err);
        }
      };

      const runEmail = async () => {
        if (!shouldRun('qontoEmail')) { updateStep('qontoEmail', 'completed'); return; }
        updateStep('qontoEmail', 'in_progress');
        try {
          const emailResponse = await callEinvoicingStep('send-email', { qontoInvoiceId, attachmentId });
          const emailData = await emailResponse.json();

          if (!emailResponse.ok) {
            updateStep('qontoEmail', 'error');
            failedAt = failedAt || 'qontoEmail';
            console.warn('[e-invoicing] Erreur send-email:', emailData.error);
          } else {
            updateStep('qontoEmail', emailData.configured ? 'completed' : 'disabled');
          }
        } catch (err) {
          updateStep('qontoEmail', 'error');
          failedAt = failedAt || 'qontoEmail';
          console.error('[e-invoicing] Erreur réseau send-email:', err);
        }
      };

      await Promise.all([runDropbox(), runDrive(), runEmail()]);

      qontoRetryStateRef.current = failedAt
        ? { numeroFacture, dateEmission, qontoInvoiceId, attachmentId, resumeFrom: failedAt }
        : null;
    } catch (qontoErr) {
      console.error('[e-invoicing] Erreur réseau:', qontoErr);
      updateStep(resumeFrom === 'db' ? 'db' : resumeFrom, 'error');
      qontoRetryStateRef.current = { numeroFacture, dateEmission, qontoInvoiceId, attachmentId, resumeFrom };
    }
  }, [updateStep, disableRemainingQontoSteps]);

  // Garde-fou de reprise pour le flux Qonto des factures internes (indépendant de
  // qontoRetryStateRef, propre aux factures externes).
  const qontoRetryStateInterneRef = useRef(null);

  const disableRemainingQontoInterneSteps = useCallback((fromIndex, status) => {
    for (let i = fromIndex; i < QONTO_INTERNE_STEP_IDS.length; i++) updateStep(QONTO_INTERNE_STEP_IDS[i], status);
  }, [updateStep]);

  /**
   * Exécute (ou reprend) la séquence d'émission Qonto pour une facture interne : émission
   * (avec les identifiants Qonto du consultant) -> attente PDF -> upload Dropbox / Drive en
   * parallèle. Pas d'étape "db" séparée (chargée côté serveur dans l'étape "emit") ni
   * d'étape email.
   *
   * @param {string} numeroFacture
   * @param {string} dateEmission
   * @param {Object} [resume] - { qontoInvoiceId, attachmentId, resumeFrom, ...meta }
   * @param {Object} [meta] - Données à conserver dans l'état de reprise (ex: factureId,
   *   currentStatut) pour permettre à "Réessayer" de finir la mise à jour du statut sans
   *   dépendre de l'état du composant (qui peut avoir changé entre-temps).
   * @returns {Promise<boolean>} true si toutes les étapes ont réussi
   */
  const runQontoFlowInterne = useCallback(async (numeroFacture, dateEmission, resume = null, meta = {}) => {
    const callStep = async (step, extra = {}) => fetchWithAuth(VERCEL_FUNCTIONS.E_INVOICING_INTERNE, {
      method: 'POST',
      body: JSON.stringify({ step, numeroFacture, dateEmission, ...extra })
    });

    let qontoInvoiceId = resume?.qontoInvoiceId || null;
    let attachmentId = resume?.attachmentId || null;
    const resumeFrom = resume?.resumeFrom || 'qontoEmit';
    const resumeOrder = ['qontoEmit', 'qontoWait', 'parallel'];
    const normalize = (step) => (PARALLEL_QONTO_INTERNE_STEPS.includes(step) ? 'parallel' : step);
    const shouldRun = (step) => resumeOrder.indexOf(normalize(step)) >= resumeOrder.indexOf(normalize(resumeFrom));

    try {
      // Étape "qontoEmit"
      if (shouldRun('qontoEmit')) {
        updateStep('qontoEmit', 'in_progress');
        const emitResponse = await callStep('emit');
        const emitData = await emitResponse.json();

        if (!emitResponse.ok) {
          updateStep('qontoEmit', 'error');
          disableRemainingQontoInterneSteps(1, 'error');
          console.warn('[e-invoicing-interne] Erreur emit:', emitData.error);
          qontoRetryStateInterneRef.current = { numeroFacture, dateEmission, resumeFrom: 'qontoEmit', ...meta };
          return false;
        }
        updateStep('qontoEmit', 'completed');
        qontoInvoiceId = emitData.qontoInvoiceId;
        attachmentId = emitData.attachmentId || attachmentId;
      } else {
        updateStep('qontoEmit', 'completed');
      }

      // Étape "qontoWait"
      if (shouldRun('qontoWait')) {
        updateStep('qontoWait', 'in_progress');
        const waitResponse = await callStep('wait-pdf', { qontoInvoiceId });
        const waitData = await waitResponse.json();

        if (!waitResponse.ok) {
          updateStep('qontoWait', 'error');
          disableRemainingQontoInterneSteps(2, 'error');
          console.warn('[e-invoicing-interne] Erreur wait-pdf:', waitData.error);
          qontoRetryStateInterneRef.current = { numeroFacture, dateEmission, qontoInvoiceId, resumeFrom: 'qontoWait', ...meta };
          return false;
        }
        updateStep('qontoWait', 'completed');
        attachmentId = waitData.attachmentId || attachmentId;
      } else {
        updateStep('qontoWait', 'completed');
      }

      // Étapes "qontoDropbox" et "qontoDrive" : indépendantes, lancées en parallèle.
      let failedAt = null;

      const runDropbox = async () => {
        if (!shouldRun('qontoDropbox')) { updateStep('qontoDropbox', 'completed'); return; }
        updateStep('qontoDropbox', 'in_progress');
        try {
          const dropboxResponse = await callStep('upload-dropbox', { qontoInvoiceId, attachmentId });
          const dropboxData = await dropboxResponse.json();

          if (!dropboxResponse.ok) {
            updateStep('qontoDropbox', 'error');
            failedAt = failedAt || 'qontoDropbox';
            console.warn('[e-invoicing-interne] Erreur upload-dropbox:', dropboxData.error);
          } else {
            updateStep('qontoDropbox', dropboxData.configured ? 'completed' : 'disabled');
          }
        } catch (err) {
          updateStep('qontoDropbox', 'error');
          failedAt = failedAt || 'qontoDropbox';
          console.error('[e-invoicing-interne] Erreur réseau upload-dropbox:', err);
        }
      };

      const runDrive = async () => {
        if (!shouldRun('qontoDrive')) { updateStep('qontoDrive', 'completed'); return; }
        updateStep('qontoDrive', 'in_progress');
        try {
          const driveResponse = await callStep('upload-drive', { qontoInvoiceId, attachmentId });
          const driveData = await driveResponse.json();

          if (!driveResponse.ok) {
            updateStep('qontoDrive', 'error');
            failedAt = failedAt || 'qontoDrive';
            console.warn('[e-invoicing-interne] Erreur upload-drive:', driveData.error);
          } else {
            updateStep('qontoDrive', driveData.configured ? 'completed' : 'disabled');
          }
        } catch (err) {
          updateStep('qontoDrive', 'error');
          failedAt = failedAt || 'qontoDrive';
          console.error('[e-invoicing-interne] Erreur réseau upload-drive:', err);
        }
      };

      await Promise.all([runDropbox(), runDrive()]);

      qontoRetryStateInterneRef.current = failedAt
        ? { numeroFacture, dateEmission, qontoInvoiceId, attachmentId, resumeFrom: failedAt, ...meta }
        : null;

      return !failedAt;
    } catch (qontoErr) {
      console.error('[e-invoicing-interne] Erreur réseau:', qontoErr);
      updateStep(resumeFrom === 'parallel' ? 'qontoDropbox' : resumeFrom, 'error');
      qontoRetryStateInterneRef.current = { numeroFacture, dateEmission, qontoInvoiceId, attachmentId, resumeFrom, ...meta };
      return false;
    }
  }, [updateStep, disableRemainingQontoInterneSteps]);

  /**
   * Mise à jour du statut ("A envoyer" -> "Emise") via l'endpoint dédié, indépendant du
   * canal d'émission utilisé (historique et/ou Qonto). Idempotent (no-op si déjà émise),
   * donc sûr à rappeler depuis "Réessayer" même si cette étape avait déjà réussi.
   */
  const runStatusUpdate = useCallback(async (numeroFacture, dateEmission) => {
    updateStep('status', 'in_progress');

    try {
      const statutResponse = await fetchWithAuth('/.netlify/functions/updateFactureExterneStatut', {
        method: 'POST',
        body: JSON.stringify({ numeroFacture, dateEmission })
      });
      const statutData = await statutResponse.json();

      if (statutResponse.ok) {
        updateStep('status', 'completed');
      } else {
        updateStep('status', 'error');
        console.warn('[status] Erreur mise à jour statut:', statutData.error);
      }
    } catch (statutErr) {
      console.error('[status] Erreur réseau:', statutErr);
      updateStep('status', 'error');
    }
  }, [updateStep]);

  // Handler générique de reprise pour la modale de progression : une seule modale à la fois,
  // mais deux flux Qonto distincts peuvent l'utiliser (facture externe "Envoi email", facture
  // interne "Télécharger/Uploader") — on reprend celui dont l'état de reprise est renseigné.
  const handleRetrySendEmail = useCallback(async () => {
    const resumeInterne = qontoRetryStateInterneRef.current;
    const resumeExterne = qontoRetryStateRef.current;

    if (!resumeInterne && !resumeExterne) {
      showToast('Rien à reprendre côté Qonto — fermez la fenêtre et renvoyez la facture si besoin', 'error');
      return;
    }

    // Reset visuel complet : plus aucune étape en rouge ni header en mode erreur, comme si
    // le process n'avait jamais échoué — seules les étapes déjà réussies restent acquises.
    setProgressModal(prev => ({
      ...prev,
      errorMessage: null,
      steps: prev.steps.map(s => (s.status === 'error' ? { ...s, status: 'pending' } : s))
    }));

    if (resumeInterne) {
      const success = await runQontoFlowInterne(resumeInterne.numeroFacture, resumeInterne.dateEmission, resumeInterne, {
        factureId: resumeInterne.factureId,
        currentStatut: resumeInterne.currentStatut
      });

      if (success && resumeInterne.factureId) {
        if (resumeInterne.currentStatut === 'A envoyer') {
          updateStep('status', 'in_progress');
          try {
            const updated = await updateFactureInterne(resumeInterne.factureId, { 'Statut': 'Emise', 'Date émission': resumeInterne.dateEmission });
            setFacturesInternes(prev => prev.map(f => f.id === updated.id ? updated : f));
            updateStep('status', 'completed');
          } catch (e) {
            updateStep('status', 'error');
          }
        } else {
          updateStep('status', 'completed');
        }
      }
    } else {
      await runQontoFlow(resumeExterne.numeroFacture, resumeExterne.dateEmission, resumeExterne);
      // La mise à jour du statut est indépendante de Qonto : si elle avait aussi échoué lors du
      // premier passage (ex: incident réseau ayant touché plusieurs appels), on la retente ici.
      await runStatusUpdate(resumeExterne.numeroFacture, resumeExterne.dateEmission);
    }

    // Sans ce rechargement, la modale se ferme sur des données obsolètes (statut affiché
    // avant la reprise) tant que la page n'est pas rafraîchie manuellement.
    await loadData();
  }, [runQontoFlow, runQontoFlowInterne, runStatusUpdate, setProgressModal, setFacturesInternes, updateStep, showToast, loadData]);

  // === HANDLERS DE TRI ===

  const handleSort = useCallback((column) => {
    if (sortColumn === column) {
      dispatch({ type: ACTIONS.SET_SORT_DIRECTION, payload: sortDirection === 'asc' ? 'desc' : 'asc' });
    } else {
      dispatch({ type: ACTIONS.SET_SORT_COLUMN, payload: column });
      dispatch({ type: ACTIONS.SET_SORT_DIRECTION, payload: 'asc' });
    }
  }, [sortColumn, sortDirection, dispatch, ACTIONS]);

  // === HANDLERS DÉTAIL ===

  const openDetail = useCallback((facture, readOnly = false) => {
    const numero = facture.fields?.['N° facture'] || facture.fields?.['Numéro de facture'];
    if (!numero) {
      setError('Numéro de facture introuvable');
      return;
    }
    dispatch({ type: ACTIONS.OPEN_DETAIL_MODAL, payload: { facture: numero, readOnly } });
  }, [dispatch, ACTIONS, setError]);

  // === HANDLERS EMAIL ===

  const handleSendEmail = useCallback((facture) => {
    const prestId = facture.fields?.['Prestataire porteur']?.[0] || facture.fields?.Prestataires?.[0];
    if (!prestId) { showToast('Prestataire manquant', 'error'); return; }

    const prestataire = prestataires.find(p => p.id === prestId);
    if (!prestataire) { showToast('Prestataire introuvable', 'error'); return; }
    if (!prestataire.fields?.Email) { showToast('Email du prestataire manquant', 'error'); return; }
    if (!facture.fields?.['N° facture']) { showToast('Numéro de facture manquant', 'error'); return; }

    dispatch({ type: ACTIONS.OPEN_DATE_EMISSION_MODAL, payload: { facture, prestataire } });
  }, [prestataires, showToast, dispatch, ACTIONS]);

  const handleConfirmDateEmission = useCallback(async (selectedDate) => {
    try {
      const factureWithNewDate = {
        ...dateEmissionFacture,
        fields: { ...dateEmissionFacture.fields, 'Date émission': selectedDate }
      };
      dispatch({
        type: ACTIONS.OPEN_EMAIL_PREVIEW_MODAL,
        payload: { facture: factureWithNewDate, prestataire: dateEmissionPrestataire }
      });
    } catch (err) {
      console.error('Erreur préparation email:', err);
      showToast('Erreur lors de la préparation de l\'email', 'error');
    }
  }, [dateEmissionFacture, dateEmissionPrestataire, dispatch, showToast, ACTIONS]);

  const handleConfirmSendEmail = useCallback(async () => {
    const numeroFacture = emailFacture.fields?.['N° facture'];
    const dateEmission = emailFacture.fields?.['Date émission'];

    dispatch({ type: ACTIONS.CLOSE_EMAIL_PREVIEW_MODAL });

    setProgressModal({
      isOpen: true,
      title: `Envoi facture ${numeroFacture}`,
      numeroFacture,
      steps: [
        { id: 'pdf', label: 'Génération du PDF interne', status: 'in_progress', weight: 2 },
        { id: 'drive', label: 'Upload sur Google Drive (PDF interne)', status: 'pending', weight: 1 },
        { id: 'email', label: 'Envoi de l\'email', status: 'pending', weight: 1 },
        { id: 'db', label: 'Chargement des données', status: 'pending', weight: 1 },
        { id: 'qontoEmit', label: 'Envoi vers Qonto', status: 'pending', weight: 2 },
        { id: 'qontoWait', label: 'Attente du PDF Qonto', status: 'pending', weight: 3 },
        { id: 'qontoDropbox', label: 'Upload Dropbox (PDF Qonto)', status: 'pending', weight: 1 },
        { id: 'qontoDrive', label: 'Upload Drive (PDF Qonto)', status: 'pending', weight: 1 },
        { id: 'qontoEmail', label: 'Envoi de l\'email (PDF Qonto)', status: 'pending', weight: 1 },
        { id: 'status', label: 'Mise à jour du statut', status: 'pending', weight: 1 }
      ],
      errorMessage: null
    });

    // Flux historique : lancé en arrière-plan, SANS bloquer l'enchaînement sur Qonto ci-dessous.
    // En régime permanent (DISABLE_DIRECT_INVOICE_EMAIL=true), cet appel ne fait plus rien
    // d'utile — mais reste sujet aux latences de vercel dev (bundling à la demande en local),
    // qui n'ont aucune raison de retarder la vraie progression (Qonto). Sa promesse est
    // réconciliée juste avant le rechargement final des données, pour rester cohérent si
    // jamais le flag est repassé à false (PDF/email/drive réellement générés).
    const historicalPromise = (async () => {
      try {
        const response = await fetchWithAuth(VERCEL_FUNCTIONS.SEND_FACTURE_EMAIL, {
          method: 'POST',
          body: JSON.stringify({ numeroFacture, dateEmission })
        });

        const data = await response.json();

        if (response.ok) {
          const disabledStatus = data.directEmailDisabled ? 'disabled' : 'completed';
          updateStep('pdf', disabledStatus);
          updateStep('drive', disabledStatus);
          updateStep('email', disabledStatus);
        } else {
          updateStep('pdf', 'error');
          setProgressModal(prev => ({ ...prev, errorMessage: data.error || 'Erreur lors de l\'envoi' }));
        }
      } catch (err) {
        console.error('Erreur envoi facture:', err);
        setProgressModal(prev => ({ ...prev, errorMessage: 'Erreur lors de l\'envoi de la facture' }));
      }
    })();

    // Facturation électronique Qonto : appels réels et séquentiels, chacun avec sa propre
    // étape (pas de simulation). Qonto s'exécute après le flux historique ci-dessus : sa
    // dernière écriture de drive_file_id (à l'étape "Upload Drive") remplace celle du PDF
    // interne le cas échéant, donc c'est bien elle qui reste affichée par la consultation.
    // En cas d'échec, le bouton "Réessayer" de la modale reprend uniquement à partir de
    // l'étape en erreur (cf. runQontoFlow / handleRetrySendEmail), sans tout relancer.
    await runQontoFlow(numeroFacture, dateEmission);
    await runStatusUpdate(numeroFacture, dateEmission);

    await historicalPromise;
    dispatch({ type: ACTIONS.RESET_EMAIL_FLOW });
    await loadData();
  }, [emailFacture, loadData, dispatch, ACTIONS, updateStep, setProgressModal, runQontoFlow, runStatusUpdate]);

  // === HANDLERS PAIEMENT ===

  const handleMarkAsPaid = useCallback(async (selectedDate) => {
    try {
      if (!selectedPaymentFacture) { showToast('Aucune facture sélectionnée', 'error'); return; }
      const updatedFacture = await updateFactureExterne(selectedPaymentFacture.id, {
        'Statut': 'Payée',
        'Date paiement': selectedDate
      });
      setFacturesExternes(prev => prev.map(f => f.id === updatedFacture.id ? updatedFacture : f));
      showToast('Facture marquée comme payée', 'success');
      dispatch({ type: ACTIONS.CLOSE_PAYMENT_MODAL });
    } catch (err) {
      console.error('Erreur marquage payée:', err);
      showToast('Erreur lors de la mise à jour', 'error');
    }
  }, [selectedPaymentFacture, showToast, dispatch, ACTIONS, setFacturesExternes]);

  const handleDatePaiementChange = useCallback(async (facture, date) => {
    try {
      const updateFn = activeTab === 'externe' ? updateFactureExterne : updateFactureInterne;
      const updated = await updateFn(facture.id, { 'Date paiement': date });
      const setFactures = activeTab === 'externe' ? setFacturesExternes : setFacturesInternes;
      setFactures(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err) {
      setError('Erreur mise à jour: ' + err.message);
    }
  }, [activeTab, setFacturesExternes, setFacturesInternes, setError]);

  const handleStatutChange = useCallback(async (facture, newStatut) => {
    try {
      const updateFn = activeTab === 'externe' ? updateFactureExterne : updateFactureInterne;
      const updateData = { 'Statut': newStatut };
      if (newStatut === 'Payée' && !facture.fields?.['Date paiement']) {
        updateData['Date paiement'] = new Date().toISOString().split('T')[0];
      }
      const updated = await updateFn(facture.id, updateData);
      const setFactures = activeTab === 'externe' ? setFacturesExternes : setFacturesInternes;
      setFactures(prev => prev.map(f => f.id === updated.id ? updated : f));
    } catch (err) {
      setError('Erreur mise à jour statut: ' + err.message);
    }
  }, [activeTab, setFacturesExternes, setFacturesInternes, setError]);

  // === HANDLERS FACTURES INTERNES ===

  const handleEditFactureInterne = useCallback(async (facture) => {
    const consultantId = facture.fields?.Consultant?.[0];
    const consultantNom = consultantsMap[consultantId]?.nom || '';
    const annee = facture.fields?.Année || null;
    const trimestre = facture.fields?.Trimestre || '';
    let montantHT = facture.fields?.['Montant total (HT)'] || 0;

    if (montantHT === 0 && consultantNom && annee && trimestre) {
      try {
        const response = await fetchWithAuth('/.netlify/functions/getCANetConsultant', {
          method: 'POST',
          body: JSON.stringify({ consultantNom, annee, trimestre })
        });
        if (response.ok) {
          const data = await response.json();
          montantHT = data.caNet !== undefined && data.caNet !== null ? data.caNet : 0;
        }
      } catch (err) {
        console.error('Erreur récupération CA net:', err);
      }
    }

    const prestataireId = facture.fields?.['prestataire_id'] || null;

    // Charger les lignes de détail depuis la DB. Ne pas se fier à facture.fields.is_manual :
    // éditer une facture générée automatiquement insère de vraies lignes sans jamais
    // positionner ce flag, donc on les récupère systématiquement plutôt que de risquer de
    // perdre un intitulé/libellés déjà saisis à une précédente édition.
    let lines = [];
    try {
      const detailsResponse = await fetchWithAuth(
        `/.netlify/functions/getFactureInterneDetails?id=${facture.id}`
      );
      if (detailsResponse.ok) {
        const detailsData = await detailsResponse.json();
        lines = detailsData.lines || [];
      }
    } catch (err) {
      console.error('Erreur récupération détails facture interne:', err);
    }
    // Fallback : une ligne synthétique depuis le montant HT si aucun détail stocké
    if (lines.length === 0) {
      lines = [{ description: facture.fields?.['Intitulé'] || '', montant: montantHT }];
    }

    dispatch({
      type: ACTIONS.OPEN_EDIT_FACTURE_INTERNE_MODAL,
      payload: {
        id: facture.id,
        consultant: consultantId,
        consultantNom,
        annee,
        trimestre,
        numeroFacture: facture.fields?.['N° facture'] || '',
        montantHT,
        tauxTVA: facture.fields?.['Taux TVA (%)'] ?? 20,
        intitule: facture.fields?.['Intitulé'] || '',
        lines,
        destinataireType: prestataireId ? 'prestataire' : 'ephyco',
        prestataireId,
        statut: facture.fields?.Statut || 'Brouillon'
      }
    });
  }, [consultantsMap, dispatch, ACTIONS]);

  const handleCloseEditFactureInterneModal = useCallback(() => {
    dispatch({ type: ACTIONS.CLOSE_EDIT_FACTURE_INTERNE_MODAL });
  }, [dispatch, ACTIONS]);

  const handleSaveFactureInterne = useCallback(async () => {
    try {
      // dateEmission n'est plus éditable ici : elle est fixée exclusivement au moment du
      // téléchargement (handleConfirmDownloadFactureInterne), seule date qui fait foi.
      const { id, lines, tauxTVA, intitule, prestataireId } = editingFactureInterne;
      const response = await fetchWithAuth('/.netlify/functions/saveFactureInterneDetails', {
        method: 'POST',
        body: JSON.stringify({ factureId: id, lines, tauxTVA, intitule, prestataireId }),
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || 'Erreur sauvegarde');
      }
      showToast('Facture mise à jour', 'success');
      await loadData();
      handleCloseEditFactureInterneModal();
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      showToast('Erreur lors de la sauvegarde', 'error');
    }
  }, [editingFactureInterne, loadData, showToast, handleCloseEditFactureInterneModal]);

  // === HANDLERS TÉLÉCHARGEMENT ===

  const handleDownloadFactureInterne = useCallback((facture) => {
    const numero = facture.fields?.['Numéro de facture'] || facture.fields?.['N° facture'];
    if (!numero) { showToast('Numéro de facture introuvable', 'error'); return; }
    dispatch({ type: ACTIONS.OPEN_DATE_DOWNLOAD_INTERNE_MODAL, payload: facture });
  }, [showToast, dispatch, ACTIONS]);

  const handleConfirmDownloadFactureInterne = useCallback(async (selectedDate) => {
    if (!selectedDownloadFactureInterne) return;
    const numero = selectedDownloadFactureInterne.fields?.['Numéro de facture'] || selectedDownloadFactureInterne.fields?.['N° facture'];
    const consultantId = selectedDownloadFactureInterne.fields?.Consultant?.[0];
    const consultant = consultantsMap[consultantId];

    // Bascule par consultant (le temps de la transition) : si la facturation électronique
    // Qonto est activée pour ce consultant, on émet via son propre compte Qonto au lieu du
    // flux historique (génération PDF interne + upload). Cf. handleRetrySendEmail pour la
    // reprise après erreur (état conservé dans qontoRetryStateInterneRef).
    if (consultant?.qontoEinvoicingEnabled) {
      const factureId = selectedDownloadFactureInterne.id;
      const currentStatut = selectedDownloadFactureInterne.fields?.Statut;

      dispatch({ type: ACTIONS.CLOSE_DATE_DOWNLOAD_INTERNE_MODAL });

      setProgressModal({
        isOpen: true,
        title: `Émission facture ${numero} (Qonto)`,
        numeroFacture: numero,
        steps: [
          { id: 'qontoEmit', label: 'Envoi vers Qonto', status: 'pending', weight: 2 },
          { id: 'qontoWait', label: 'Attente du PDF Qonto', status: 'pending', weight: 3 },
          { id: 'qontoDropbox', label: 'Upload Dropbox', status: 'pending', weight: 1 },
          { id: 'qontoDrive', label: 'Upload Drive', status: 'pending', weight: 1 },
          { id: 'status', label: 'Mise à jour du statut', status: 'pending', weight: 1 }
        ],
        errorMessage: null
      });

      const success = await runQontoFlowInterne(numero, selectedDate, null, { factureId, currentStatut });

      if (success) {
        if (currentStatut === 'A envoyer') {
          updateStep('status', 'in_progress');
          try {
            const updated = await updateFactureInterne(factureId, { 'Statut': 'Emise', 'Date émission': selectedDate });
            setFacturesInternes(prev => prev.map(f => f.id === updated.id ? updated : f));
            updateStep('status', 'completed');
          } catch (e) {
            updateStep('status', 'error');
          }
        } else {
          updateStep('status', 'completed');
        }
      } else {
        setProgressModal(prev => ({ ...prev, errorMessage: prev.errorMessage || 'Échec de l\'émission via Qonto' }));
      }

      await loadData();
      return;
    }

    setProgressModal({
      isOpen: true,
      title: `Upload facture ${numero}`,
      numeroFacture: numero,
      steps: [
        { id: 'pdf', label: 'Génération du PDF', status: 'in_progress', weight: 5 },
        { id: 'drive', label: 'Upload Google Drive', status: 'pending', weight: 1 },
        { id: 'dropbox', label: 'Upload Dropbox', status: 'pending', weight: 1 },
        { id: 'status', label: 'Mise à jour statut', status: 'pending', weight: 1 }
      ],
      errorMessage: null
    });

    try {
      const response = await fetchWithAuth(VERCEL_FUNCTIONS.UPLOAD_FACTURE_INTERNE, {
        method: 'POST',
        body: JSON.stringify({ numeroFacture: numero, dateEmission: selectedDate })
      });

      dispatch({ type: ACTIONS.CLOSE_DATE_DOWNLOAD_INTERNE_MODAL });

      if (response.ok) {
        const data = await response.json();
        updateStep('pdf', 'completed');
        updateStep('drive', data.uploads?.drive === 'success' ? 'completed' : 'error');
        updateStep('dropbox', data.uploads?.dropbox === 'success' ? 'completed' : 'error');

        if (data.uploads?.drive === 'success' && data.uploads?.dropbox === 'success') {
          if (selectedDownloadFactureInterne.fields?.Statut === 'A envoyer') {
            updateStep('status', 'in_progress');
            try {
              const updated = await updateFactureInterne(selectedDownloadFactureInterne.id, { 'Statut': 'Emise', 'Date émission': selectedDate });
              setFacturesInternes(prev => prev.map(f => f.id === updated.id ? updated : f));
              updateStep('status', 'completed');
            } catch (e) {
              updateStep('status', 'error');
            }
          } else {
            updateStep('status', 'completed');
          }
        }
        await loadData();
      } else {
        const data = await response.json();
        updateStep('pdf', 'error');
        setProgressModal(prev => ({ ...prev, errorMessage: data.error || 'Erreur upload' }));
      }
    } catch (err) {
      console.error('Erreur upload facture:', err);
      setProgressModal(prev => ({ ...prev, errorMessage: 'Erreur lors de l\'upload' }));
    }
  }, [selectedDownloadFactureInterne, consultantsMap, dispatch, ACTIONS, updateStep, setProgressModal, setFacturesInternes, loadData, runQontoFlowInterne]);

  // === HANDLERS PDF ===

  const handleClosePdfViewer = useCallback(() => {
    dispatch({ type: ACTIONS.CLOSE_PDF_VIEWER });
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
  }, [dispatch, ACTIONS, pdfUrl]);

  const handleViewPdfFromDrive = useCallback(async (numeroFacture) => {
    try {
      const response = await fetchWithAuth(`/.netlify/functions/get-pdf-from-drive?numeroFacture=${numeroFacture}`);
      if (response.ok) {
        const blob = await response.blob();
        dispatch({ type: ACTIONS.OPEN_PDF_VIEWER, payload: URL.createObjectURL(blob) });
      } else if (response.status === 404) {
        const err = await response.json();
        showToast(err.message || 'PDF non disponible sur Drive', 'error');
      } else {
        showToast('Erreur récupération PDF', 'error');
      }
    } catch (err) {
      console.error('Erreur get PDF:', err);
      showToast('Erreur récupération PDF', 'error');
    }
  }, [dispatch, ACTIONS, showToast]);

  // === HANDLERS AJOUT ===

  const handleAjouterFacture = useCallback(async (data, typeFacture) => {
    try {
      const endpoint = typeFacture === 'externe'
        ? '/.netlify/functions/addFactureExterne'
        : '/.netlify/functions/addFactureInterne';
      const response = await fetchWithAuth(endpoint, {
        method: 'POST',
        body: JSON.stringify(data)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.details || result.error || 'Erreur création');
      showToast(`Facture ${typeFacture} créée`, 'success');
      dispatch({ type: ACTIONS.CLOSE_AJOUTER_MODAL });
      loadData();
    } catch (err) {
      console.error('Erreur création:', err);
      showToast(err.message, 'error');
    }
  }, [showToast, dispatch, ACTIONS, loadData]);

  return {
    // Tri
    handleSort,
    // Détail
    openDetail,
    // Email
    handleSendEmail,
    handleConfirmDateEmission,
    handleConfirmSendEmail,
    handleRetrySendEmail,
    // Paiement
    handleMarkAsPaid,
    handleDatePaiementChange,
    handleStatutChange,
    // Factures internes
    handleEditFactureInterne,
    handleCloseEditFactureInterneModal,
    handleSaveFactureInterne,
    // Téléchargement
    handleDownloadFactureInterne,
    handleConfirmDownloadFactureInterne,
    // PDF
    handleClosePdfViewer,
    handleViewPdfFromDrive,
    // Ajout
    handleAjouterFacture,
    // Helpers
    updateStep
  };
}
